import { useMemo, useState } from 'react';
import { Box, styled } from '@material-ui/core';
import { useQuery } from '@tanstack/react-query';
import { Product } from '@setvi/shared/interfaces';
import { SBase, SButton } from '@setvi/shared/components';
import { CheckIconV2 } from '@setvi/shared/components/sicons';
import { AnalyticsModules, useAnalytics } from '@setvi/shared/hooks';

import { getRfqMatchedItemsQuery } from 'Services';
import { ProductsList } from 'pages/products/components';
import { useCatalogConfig } from 'pages/products/hooks/useCatalogConfig';

import { useRFQStore } from 'pages/auto-rfq/store';

import { Footer } from '../catalog/styled';

interface ExpandedProducts extends Product {
  order?: number;
}

export const Container = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  padding: theme.spacing(2, 4, 3, 4)
}));

export const Content = styled(Box)(({ theme }) => ({
  flex: 1,
  background: '#F8F9FA',
  borderRadius: 20,
  padding: theme.spacing(4),

  '& tr': {
    background: 'white '
  }
}));

export interface PrivateLabelsProps {
  ids?: string[];
  fetch?: boolean;
  products: Product[];
  selected: Product;
  competitorName?: string;
  competitorSku?: string;
  setSelected: (product: Product) => void;
  onClose?: () => void;
}

const PrivateLabels = ({
  ids,
  fetch,
  products,
  selected,
  competitorName,
  competitorSku,
  onClose,
  setSelected
}: PrivateLabelsProps) => {
  const { captureAnalytics } = useAnalytics();
  const [exported, setExported] = useState(selected || null);
  const {
    updateAnalytic,
    analytics,
    privateLabelsSelected,
    updatePrivateLabelsSelected
  } = useRFQStore();

  const { data, isFetching } = useQuery({
    ...getRfqMatchedItemsQuery(ids),
    enabled: !!ids?.length && fetch
  });

  const apiProducts = useMemo(() => {
    if (!fetch) return products;
    if (isFetching) return [];
    const currProducts = data
      ?.map((d: any) => {
        const curr = products.find(
          (p: any) => p.id === d._id || p._id === d._id
        );
        return { ...curr, ...d, external: false };
      })
      .sort((a: ExpandedProducts, b: ExpandedProducts) =>
        a.order >= 0 ? a.order - b.order : b.percentage - a.percentage
      );

    return currProducts || [];
  }, [data, fetch, products, isFetching]);

  const { configuration } = useCatalogConfig();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const aiFilteredProducts = useMemo(
    () => (fetch ? apiProducts : products),
    [apiProducts, fetch, products]
  );

  const handleAnalytics = () => {
    const sourceIsPL = selected?.isPrivateLabel || selected?.privateLabel;
    const targetIsPL = exported?.isPrivateLabel || exported?.privateLabel;

    const analyticsBody = {
      action: 'change',
      id: exported?._id,
      sourceProductId: selected?.sku || '',
      targetProductId: exported?.sku || '',
      sourceIsPL,
      targetIsPL
    };

    if (!sourceIsPL && targetIsPL) {
      captureAnalytics(
        {
          subAction: 'Private label SWAP',
          ...analyticsBody

          // !we will add this on auto safe
          // rfqId: '3f7a5102-53c4-46b4-aed2-e123b0b1967a',
        },
        AnalyticsModules.AutoRFQItem
      );

      if (!privateLabelsSelected.includes(exported?.sku)) {
        updatePrivateLabelsSelected([exported?.sku]);
        updateAnalytic({
          privateLabelSwapped: analytics.privateLabelSwapped + 1
        });
      }

      return;
    }

    captureAnalytics(analyticsBody, AnalyticsModules.AutoRFQItem);
  };

  return (
    <SBase
      disablePadding
      style={{
        background: '#fff'
      }}
      width="90vw"
      height="90vh"
      header={{
        title: 'Private Label',
        description: `More Private Label Matches for ${competitorName || 'Product'} ${competitorSku ? `(SKU: #${competitorSku})` : ''}`,
        onClose
      }}>
      <Box
        flex={1}
        width="100%"
        height="100%"
        flexDirection="column"
        display="flex"
        justifyContent="space-between">
        <Container>
          <Content>
            <ProductsList
              headerProps={{
                columns: []
              }}
              products={aiFilteredProducts ?? []}
              config={{
                columns: configuration.product?.columns,
                grid: configuration.product?.grid,
                priceType: configuration?.price,
                stock: configuration?.product?.grid?.stock,
                manufacturerSku: configuration?.product?.grid?.manufacturerSku
              }}
              disableSelect={false}
              drawerOpen={drawerOpen}
              setDrawerOpen={setDrawerOpen}
              fetchMore={() => {}}
              isLoading={fetch && isFetching}
              simpleLoading
              selectedProducts={[exported]}
              handleProductSelect={(isChecked, _products) => {
                if (isChecked) setExported(_products[0]);
              }}
              openProductDetails={() => {}}
              productPricesLoading={false}
              aiSearchActive
              type="radio"
            />
          </Content>
        </Container>

        <Footer>
          <SButton
            color="default"
            variant="text"
            onClick={onClose}
            style={{
              borderRadius: 36,
              color: '#5D7083'
            }}>
            Cancel
          </SButton>

          <SButton
            disabled={!exported}
            tooltipTitle={!exported ? 'Please select item' : ''}
            onClick={() => {
              handleAnalytics();
              setSelected(exported);
            }}
            startIcon={
              <CheckIconV2
                style={{
                  width: 20,
                  height: 20,
                  marginBottom: 2
                }}
              />
            }
            style={{
              borderRadius: 36
            }}>
            Replace Item
          </SButton>
        </Footer>
      </Box>
    </SBase>
  );
};

export default PrivateLabels;
